import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";

/* ═══════════════════════════════════════════════════════════════
   MdmPaywall — Drop-in React paywall component for MyDocMaster
   
   Usage in your router / OTP success handler:
   
     import MdmPaywall from './MdmPaywall';
     
     // Show after OTP verification:
     const [showPaywall, setShowPaywall] = useState(false);
     
     // After OTP success:
     setShowPaywall(true);
     
     // In JSX:
     {showPaywall && <MdmPaywall onClose={() => setShowPaywall(false)} />}
     
   Props:
     onClose  — called when user clicks free trial or you call close
     show     — (optional) control visibility externally, defaults to true
   ═══════════════════════════════════════════════════════════════ */

const STORAGE_KEY = "mdm_pw_dl";
const OFFERS_URL = "https://mydocmaster.com/Pricing";
const FREE_URL = "https://mydocmaster.com";

// ── Storage helper ──
function getStore() {
  try {
    localStorage.setItem("_t", "1");
    localStorage.removeItem("_t");
    return localStorage;
  } catch (e) {
    try {
      sessionStorage.setItem("_t", "1");
      sessionStorage.removeItem("_t");
      return sessionStorage;
    } catch (e2) {
      return null;
    }
  }
}

// ── Tracking helpers ──
function px(method, event, params) {
  if (typeof window.fbq === "function") window.fbq(method, event, params || {});
}
function ga(event, params) {
  if (typeof window.gtag === "function")
    window.gtag("event", event, params || {});
}

// ── SVG Icons (inline, no external deps) ──
const CheckIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#fff"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const StarIcon = () => (
  <svg viewBox="0 0 24 24">
    <path
      fill="#F5A623"
      d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
    />
  </svg>
);
const ArrowIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);
const ShieldIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0FAA5C"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);
const TickIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0FAA5C"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const CircleTickIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0FAA5C"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M8 12l2 2 4-4" />
  </svg>
);
const DocIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
  </svg>
);
const PenIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);
const CalIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const BookIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
    <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);

// ── Features data ──
const FEATURES = [
  {
    icon: DocIcon,
    name: "Documents",
    hi: "डॉक्युमेंट्स",
    color: "bl",
    tag: "free",
  },
  {
    icon: PenIcon,
    name: "Question Paper",
    hi: "प्रश्न पत्र",
    color: "gn",
    tag: "pro",
  },
  {
    icon: CalIcon,
    name: "Timetable",
    hi: "समय सारिणी",
    color: "or",
    tag: "pro",
  },
  {
    icon: BookIcon,
    name: "Planner & More",
    hi: "प्लानर व अन्य",
    color: "pu",
    tag: "pro",
  },
];

// ── Countdown Hook ──
function useCountdown() {
  const store = useRef(getStore());
  const [time, setTime] = useState({ h: 23, m: 59, s: 59, expired: false });

  useEffect(() => {
    let deadline = store.current ? store.current.getItem(STORAGE_KEY) : null;
    if (!deadline || new Date(deadline) <= new Date()) {
      deadline = new Date(Date.now() + 86400000).toISOString();
      if (store.current) store.current.setItem(STORAGE_KEY, deadline);
    }

    const update = () => {
      const diff = Math.max(
        0,
        Math.floor((new Date(deadline) - new Date()) / 1000),
      );
      if (diff === 0) {
        setTime({ h: 0, m: 0, s: 0, expired: true });
        return;
      }
      setTime({
        h: Math.floor(diff / 3600),
        m: Math.floor((diff % 3600) / 60),
        s: diff % 60,
        expired: false,
      });
    };

    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  return time;
}

// ── Pad number ──
const pad = (n) => String(n).padStart(2, "0");

// ══════════════════════════════════════
// COMPONENT
// ══════════════════════════════════════
export default function MdmPaywall({ onClose, show = true }) {
  const [active, setActive] = useState(false);
  const countdown = useCountdown();
  const navigate = useNavigate();

  // Animate in on mount
  useEffect(() => {
    if (!show) return;
    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => setActive(true));
    });
    // Fire tracking
    px("trackCustom", "ViewPaywall");
    ga("view_paywall", { event_category: "conversion" });
    return () => cancelAnimationFrame(raf);
  }, [show]);

  const handleOffers = useCallback(() => {
    px("track", "InitiateCheckout", { content_name: "paywall_check_offers" });
    ga("begin_checkout", {
      event_category: "conversion",
      event_label: "check_offers",
    });
    // window.location.href = OFFERS_URL;
    navigate("/pricing");
  }, []);

  const handleTrial = useCallback(() => {
    px("trackCustom", "StartFreeTrial", { content_name: "paywall_free_trial" });
    ga("start_trial", {
      event_category: "conversion",
      event_label: "free_trial",
    });
    setActive(false);
    setTimeout(() => {
      if (onClose) onClose();
      // window.location.href = FREE_URL;
      navigate("/dashboard");
    }, 350);
  }, [onClose]);

  if (!show) return null;

  return (
    <>
      <style>{PAYWALL_CSS}</style>
      {/* Overlay */}
      <div className={`mdm-pw-overlay${active ? " on" : ""}`} />
      {/* Shell */}
      <div className={`mdm-pw-shell${active ? " on" : ""}`}>
        {/* ═══ HERO ═══ */}
        <section className="hero">
          <div className="logo-slot">
            {/* LOGO: <img src="..." alt="MyDocMaster" height="26" /> */}
          </div>

          <div className="status-row">
            <div className="pill">
              <div className="ck">
                <CheckIcon />
              </div>
              <span>रजिस्ट्रेशन पूरा!</span>
            </div>
            <div className="stars-mini">
              <StarIcon />
              <StarIcon />
              <StarIcon />
              <StarIcon />
              <StarIcon />
            </div>
            <div className="proof-num">
              <b>50,000+</b> शिक्षकों का भरोसा
            </div>
          </div>

          <div className="hl">
            <h1>
              सभी Features <em>Unlock</em> करें
            </h1>
          </div>
        </section>

        {/* ═══ CONTENT ═══ */}
        <div className="content">
          {/* Feature Grid */}
          <div className="fgrid">
            {FEATURES.map((f) => (
              <div className="fcell" key={f.name}>
                <div className={`fcell-ico ${f.color}`}>
                  <f.icon />
                </div>
                <div className="fcell-txt">
                  <div className="fcell-name">{f.name}</div>
                  <div className="fcell-hi">{f.hi}</div>
                </div>
                <div className={`fcell-tag ${f.tag}`}>
                  {f.tag === "free" ? "FREE" : "PRO"}
                </div>
              </div>
            ))}
          </div>

          {/* Urgency + Price */}
          <div className={`urgency${countdown.expired ? " expired" : ""}`}>
            <div className="urg-left">
              <div className="urg-title">
                Registration Offer! <span>— सीमित समय</span>
              </div>
              <div className="cd">
                <div className="cd-u">
                  <div className="cd-n">{pad(countdown.h)}</div>
                  <div className="cd-l">Hrs</div>
                </div>
                <div className="cd-sep">:</div>
                <div className="cd-u">
                  <div className="cd-n">{pad(countdown.m)}</div>
                  <div className="cd-l">Min</div>
                </div>
                <div className="cd-sep">:</div>
                <div className="cd-u">
                  <div className="cd-n">{pad(countdown.s)}</div>
                  <div className="cd-l">Sec</div>
                </div>
              </div>
              <div className="cd-exp">⏰ Offer समाप्त — फिर भी देखें!</div>
            </div>
            <div className="urg-price">
              <div className="urg-amt">₹21</div>
              <div className="urg-per">/month से</div>
              <div className="urg-yr">₹250/yr</div>
            </div>
          </div>

          {/* CTAs */}
          <div className="cta-wrap">
            <button className="cta-main" onClick={handleOffers}>
              Offers देखें <ArrowIcon />
            </button>
            <div className="cta-sec-wrap">
              <button className="cta-sec" onClick={handleTrial}>
                Free modules से शुरू करें <span className="arr">→</span>
              </button>
            </div>
          </div>
        </div>

        {/* ═══ TRUST ═══ */}
        <footer className="trust">
          <div className="trust-row">
            <div className="trust-i">
              <ShieldIcon />
              Secure Payment
            </div>
            <div className="trust-i">
              <TickIcon />
              Instant Activation
            </div>
            <div className="trust-i">
              <CircleTickIcon />
              One-Time Payment
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

// ══════════════════════════════════════
// SCOPED CSS (injected via <style> tag)
// ══════════════════════════════════════
const PAYWALL_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@500;700;800&family=Noto+Sans+Devanagari:wght@500;600;700&display=swap');

.mdm-pw-overlay{position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(15,20,40,0.6);backdrop-filter:blur(4px);-webkit-backdrop-filter:blur(4px);z-index:999998;opacity:0;transition:opacity .3s ease}
.mdm-pw-overlay.on{opacity:1}

.mdm-pw-shell,.mdm-pw-shell *,.mdm-pw-shell *::before,.mdm-pw-shell *::after{margin:0;padding:0;box-sizing:border-box}
.mdm-pw-shell{position:fixed;z-index:999999;font-family:"Outfit","Noto Sans Devanagari",-apple-system,sans-serif;color:#1A1D2E;-webkit-font-smoothing:antialiased;background:#F5F6FA;opacity:0;transition:opacity .35s ease,transform .35s ease;overflow-y:auto;-webkit-overflow-scrolling:touch}
.mdm-pw-shell::-webkit-scrollbar{width:4px}
.mdm-pw-shell::-webkit-scrollbar-track{background:transparent}
.mdm-pw-shell::-webkit-scrollbar-thumb{background:rgba(0,0,0,.12);border-radius:4px}

/* Mobile: bottom sheet */
@media(max-width:767px){
  .mdm-pw-shell{bottom:0;left:0;right:0;width:100%;max-height:96vh;max-height:96dvh;border-radius:20px 20px 0 0;box-shadow:0 -8px 40px rgba(0,0,0,.2);transform:translateY(100%)}
  .mdm-pw-shell.on{opacity:1;transform:translateY(0)}
}
/* Tablet: centered modal */
@media(min-width:768px) and (max-width:1023px){
  .mdm-pw-shell{top:50%;left:50%;transform:translate(-50%,-50%) scale(.95);width:440px;max-height:90vh;border-radius:24px;box-shadow:0 20px 60px rgba(0,0,0,.18)}
  .mdm-pw-shell.on{opacity:1;transform:translate(-50%,-50%) scale(1)}
}
/* Desktop: centered modal wider */
@media(min-width:1024px){
  .mdm-pw-shell{top:50%;left:50%;transform:translate(-50%,-50%) scale(.95);width:480px;max-height:88vh;border-radius:24px;box-shadow:0 24px 80px rgba(0,0,0,.16)}
  .mdm-pw-shell.on{opacity:1;transform:translate(-50%,-50%) scale(1)}
}

/* ── Hero ── */
.mdm-pw-shell .hero{background:linear-gradient(168deg,#0052CC 0%,#002B6B 50%,#001A45 100%);padding:24px 22px 40px;text-align:center;position:relative;overflow:hidden}
.mdm-pw-shell .hero::before{content:"";position:absolute;top:-50%;right:-25%;width:280px;height:280px;border-radius:50%;background:radial-gradient(circle,rgba(255,107,44,.1) 0%,transparent 70%);pointer-events:none}
@media(min-width:768px){.mdm-pw-shell .hero{padding:32px 32px 48px}}
@media(min-width:1024px){.mdm-pw-shell .hero{padding:36px 40px 48px}}

.mdm-pw-shell .logo-slot{height:26px;margin-bottom:14px;display:flex;justify-content:center;align-items:center}
@media(min-width:768px){.mdm-pw-shell .logo-slot{height:30px;margin-bottom:16px}}

.mdm-pw-shell .status-row{display:flex;align-items:center;justify-content:center;gap:10px;margin-bottom:14px;flex-wrap:wrap;animation:mdmFd .5s ease-out}
@media(min-width:768px){.mdm-pw-shell .status-row{gap:12px;margin-bottom:16px}}
.mdm-pw-shell .pill{display:inline-flex;align-items:center;gap:6px;background:rgba(15,170,92,.15);border:1px solid rgba(15,170,92,.3);border-radius:100px;padding:5px 14px}
.mdm-pw-shell .pill .ck{width:16px;height:16px;background:#0FAA5C;border-radius:50%;display:grid;place-items:center;flex-shrink:0}
.mdm-pw-shell .pill .ck svg{width:10px;height:10px}
.mdm-pw-shell .pill span{color:#B0F0D0;font-size:12px;font-weight:600}
@media(min-width:768px){.mdm-pw-shell .pill{padding:6px 16px} .mdm-pw-shell .pill span{font-size:13px}}
.mdm-pw-shell .stars-mini{display:flex;gap:1px}
.mdm-pw-shell .stars-mini svg{width:14px;height:14px}
@media(min-width:768px){.mdm-pw-shell .stars-mini svg{width:16px;height:16px}}
.mdm-pw-shell .proof-num{color:rgba(255,255,255,.7);font-size:11px;font-weight:500;letter-spacing:.03em}
.mdm-pw-shell .proof-num b{color:#fff;font-weight:700}
@media(min-width:768px){.mdm-pw-shell .proof-num{font-size:12px}}

/* ── Headline ── */
.mdm-pw-shell .hl{animation:mdmFd .55s ease-out .05s both}
.mdm-pw-shell .hl h1{font-size:24px;font-weight:800;color:#fff;line-height:1.2;letter-spacing:-.01em}
.mdm-pw-shell .hl h1 em{font-style:normal;color:#FF6B2C}
@media(min-width:768px){.mdm-pw-shell .hl h1{font-size:28px}}
@media(min-width:1024px){.mdm-pw-shell .hl h1{font-size:30px}}

/* ── Content ── */
.mdm-pw-shell .content{padding:0 16px 24px;margin-top:-20px;position:relative;z-index:2}
@media(min-width:768px){.mdm-pw-shell .content{padding:0 28px 28px;margin-top:-24px}}
@media(min-width:1024px){.mdm-pw-shell .content{padding:0 36px 32px}}

/* ── Feature grid ── */
.mdm-pw-shell .fgrid{background:#fff;border-radius:16px;padding:16px;box-shadow:0 4px 20px rgba(0,40,100,.07);display:grid;grid-template-columns:1fr 1fr;gap:10px;margin-bottom:12px;animation:mdmFu .45s ease-out .1s both}
@media(min-width:768px){.mdm-pw-shell .fgrid{padding:20px;gap:12px;margin-bottom:16px}}
.mdm-pw-shell .fcell{display:flex;align-items:center;gap:10px;padding:10px;border-radius:12px;background:#F5F6FA;position:relative}
@media(min-width:768px){.mdm-pw-shell .fcell{padding:12px;gap:12px}}
.mdm-pw-shell .fcell-ico{width:32px;height:32px;border-radius:8px;display:grid;place-items:center;flex-shrink:0}
.mdm-pw-shell .fcell-ico svg{width:16px;height:16px;fill:none}
@media(min-width:768px){.mdm-pw-shell .fcell-ico{width:36px;height:36px} .mdm-pw-shell .fcell-ico svg{width:18px;height:18px}}
.mdm-pw-shell .fcell-ico.bl{background:#E8F0FE} .mdm-pw-shell .fcell-ico.bl svg{stroke:#0052CC}
.mdm-pw-shell .fcell-ico.gn{background:#E6F9F0} .mdm-pw-shell .fcell-ico.gn svg{stroke:#0A8F4F}
.mdm-pw-shell .fcell-ico.or{background:#FFF0E6} .mdm-pw-shell .fcell-ico.or svg{stroke:#D4600A}
.mdm-pw-shell .fcell-ico.pu{background:#F0E8FE} .mdm-pw-shell .fcell-ico.pu svg{stroke:#7C3AED}
.mdm-pw-shell .fcell-txt{flex:1;min-width:0}
.mdm-pw-shell .fcell-name{font-size:12px;font-weight:600;color:#1A1D2E;line-height:1.25}
.mdm-pw-shell .fcell-hi{font-size:10px;color:#6B7280;font-weight:500;margin-top:1px}
@media(min-width:768px){.mdm-pw-shell .fcell-name{font-size:13px} .mdm-pw-shell .fcell-hi{font-size:11px}}
.mdm-pw-shell .fcell-tag{position:absolute;top:6px;right:6px;font-size:7px;font-weight:700;text-transform:uppercase;letter-spacing:.06em;padding:2px 5px;border-radius:4px}
@media(min-width:768px){.mdm-pw-shell .fcell-tag{font-size:8px;padding:3px 6px}}
.mdm-pw-shell .fcell-tag.free{background:#E6F9F0;color:#0A8F4F}
.mdm-pw-shell .fcell-tag.pro{background:#F3F4F6;color:#9CA3AF}

/* ── Urgency ── */
.mdm-pw-shell .urgency{background:linear-gradient(135deg,#FFF5E6 0%,#FFFAF0 100%);border:1px solid rgba(245,166,35,.2);border-radius:12px;padding:12px 14px;display:flex;align-items:center;gap:10px;margin-bottom:14px;animation:mdmFu .45s ease-out .15s both}
@media(min-width:768px){.mdm-pw-shell .urgency{padding:16px 20px;margin-bottom:16px;gap:12px}}
.mdm-pw-shell .urg-left{flex:1;min-width:0}
.mdm-pw-shell .urg-title{font-size:12px;font-weight:700;color:#78350F;margin-bottom:4px}
.mdm-pw-shell .urg-title span{font-weight:600;color:#92400E}
@media(min-width:768px){.mdm-pw-shell .urg-title{font-size:13px}}
.mdm-pw-shell .cd{display:flex;gap:4px;align-items:baseline}
.mdm-pw-shell .cd-u{text-align:center;min-width:30px}
.mdm-pw-shell .cd-n{font-size:16px;font-weight:800;color:#92400E;font-variant-numeric:tabular-nums;line-height:1}
.mdm-pw-shell .cd-l{font-size:7px;font-weight:700;text-transform:uppercase;letter-spacing:.08em;color:#B45309;margin-top:1px}
.mdm-pw-shell .cd-sep{font-size:14px;font-weight:800;color:#D97706;line-height:1}
@media(min-width:768px){.mdm-pw-shell .cd-n{font-size:18px}}
.mdm-pw-shell .cd-exp{font-size:11px;font-weight:600;color:#DC2626;display:none}
.mdm-pw-shell .urgency.expired .cd{display:none}
.mdm-pw-shell .urgency.expired .cd-exp{display:block}
.mdm-pw-shell .urg-price{flex-shrink:0;text-align:center;border-left:1px solid rgba(245,166,35,.2);padding-left:12px}
@media(min-width:768px){.mdm-pw-shell .urg-price{padding-left:16px}}
.mdm-pw-shell .urg-amt{font-size:20px;font-weight:800;color:#78350F;line-height:1}
.mdm-pw-shell .urg-per{font-size:10px;color:#92400E;font-weight:600}
.mdm-pw-shell .urg-yr{font-size:9px;color:#B45309;margin-top:1px}
@media(min-width:768px){.mdm-pw-shell .urg-amt{font-size:22px} .mdm-pw-shell .urg-per{font-size:11px}}

/* ── CTAs ── */
.mdm-pw-shell .cta-wrap{animation:mdmFu .45s ease-out .2s both}
.mdm-pw-shell .cta-main{display:flex;align-items:center;justify-content:center;gap:8px;width:100%;padding:16px 24px;background:linear-gradient(135deg,#FF6B2C 0%,#E05A1A 100%);color:#fff;font-family:"Outfit",sans-serif;font-size:16px;font-weight:700;border:none;border-radius:14px;cursor:pointer;text-decoration:none;letter-spacing:.01em;box-shadow:0 5px 20px rgba(255,107,44,.3),0 2px 6px rgba(0,0,0,.06);-webkit-tap-highlight-color:transparent;position:relative;overflow:hidden;transition:transform .15s ease,box-shadow .15s ease}
@media(min-width:768px){.mdm-pw-shell .cta-main{padding:18px 28px;font-size:17px;border-radius:16px}}
.mdm-pw-shell .cta-main::after{content:"";position:absolute;top:0;left:-100%;width:100%;height:100%;background:linear-gradient(90deg,transparent,rgba(255,255,255,.12),transparent);animation:mdmSh 3.5s ease-in-out infinite}
.mdm-pw-shell .cta-main:hover{transform:translateY(-1px);box-shadow:0 7px 28px rgba(255,107,44,.35)}
.mdm-pw-shell .cta-main:active{transform:scale(.98);box-shadow:0 3px 10px rgba(255,107,44,.25)}
.mdm-pw-shell .cta-main svg{width:18px;height:18px;flex-shrink:0;transition:transform .15s ease}
.mdm-pw-shell .cta-main:hover svg{transform:translateX(3px)}

.mdm-pw-shell .cta-sec-wrap{text-align:center;margin-top:12px}
@media(min-width:768px){.mdm-pw-shell .cta-sec-wrap{margin-top:14px}}
.mdm-pw-shell .cta-sec{color:#6B7280;font-size:12.5px;font-weight:500;text-decoration:none;-webkit-tap-highlight-color:transparent;padding:6px 4px;display:inline-block;transition:color .15s ease;background:none;border:none;cursor:pointer;font-family:"Outfit",sans-serif}
@media(min-width:768px){.mdm-pw-shell .cta-sec{font-size:13px}}
.mdm-pw-shell .cta-sec:hover{color:#0052CC}
.mdm-pw-shell .cta-sec .arr{display:inline-block;margin-left:3px;transition:transform .15s ease}
.mdm-pw-shell .cta-sec:hover .arr{transform:translateX(3px)}

/* ── Trust ── */
.mdm-pw-shell .trust{text-align:center;padding:14px 18px 24px;animation:mdmFu .45s ease-out .25s both}
@media(min-width:768px){.mdm-pw-shell .trust{padding:16px 28px 24px}}
.mdm-pw-shell .trust-row{display:flex;justify-content:center;gap:14px;flex-wrap:wrap}
@media(min-width:768px){.mdm-pw-shell .trust-row{gap:20px}}
.mdm-pw-shell .trust-i{display:flex;align-items:center;gap:3px;font-size:10px;font-weight:600;color:#6B7280}
.mdm-pw-shell .trust-i svg{width:12px;height:12px}
@media(min-width:768px){.mdm-pw-shell .trust-i{font-size:11px;gap:4px} .mdm-pw-shell .trust-i svg{width:13px;height:13px}}

/* ── Tiny screens ── */
@media(max-width:360px){
  .mdm-pw-shell .hero{padding:20px 16px 32px}
  .mdm-pw-shell .hl h1{font-size:21px}
  .mdm-pw-shell .fgrid{padding:12px;gap:8px}
  .mdm-pw-shell .fcell{padding:8px;gap:8px}
  .mdm-pw-shell .fcell-name{font-size:11px}
  .mdm-pw-shell .content{padding:0 12px 20px}
  .mdm-pw-shell .cta-main{padding:14px 20px;font-size:15px}
  .mdm-pw-shell .urg-amt{font-size:18px}
}
/* ── Short viewports ── */
@media(max-height:640px) and (max-width:767px){
  .mdm-pw-shell .hero{padding:18px 20px 32px}
  .mdm-pw-shell .logo-slot{height:22px;margin-bottom:10px}
  .mdm-pw-shell .status-row{margin-bottom:10px;gap:6px}
  .mdm-pw-shell .pill{padding:4px 10px} .mdm-pw-shell .pill span{font-size:11px}
  .mdm-pw-shell .hl h1{font-size:22px}
  .mdm-pw-shell .fgrid{padding:12px;gap:6px;margin-bottom:10px}
  .mdm-pw-shell .fcell{padding:8px;gap:8px}
  .mdm-pw-shell .urgency{padding:10px 12px;margin-bottom:10px}
  .mdm-pw-shell .cta-main{padding:14px 20px}
  .mdm-pw-shell .cta-sec-wrap{margin-top:8px}
  .mdm-pw-shell .trust{padding:10px 16px 16px}
}

/* ── Animations ── */
@keyframes mdmFd{from{opacity:0;transform:translateY(-8px)}to{opacity:1;transform:translateY(0)}}
@keyframes mdmFu{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
@keyframes mdmSh{0%{left:-100%}40%{left:100%}100%{left:100%}}
`;
